import {
  faAnchor,
  faAngleDown,
  faCalculator,
  faCalendarDays,
  faCalendarXmark,
  faEdit,
  faHourglassEnd,
  faMinusCircle,
  faPlay,
  faShareNodes,
  faTag,
  faUserGroup,
  faUserTie,
  faUsers,
} from '@fortawesome/pro-regular-svg-icons';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Box } from 'src/components/Box';
import { Button } from 'src/components/Button';
import { Card } from 'src/components/Card';
import { Chip } from 'src/components/Chip';
import { Divider } from 'src/components/Divider';
import { DropdownMenu } from 'src/components/DropdownMenu';
import { Icon, IconDefinition } from 'src/components/Icon';
import { IconButton } from 'src/components/IconButton';
import { ListItem } from 'src/components/ListItem';
import { Stack } from 'src/components/Stack';
import { Tooltip } from 'src/components/Tooltip';
import { Typography } from 'src/components/Typography';
import { Collapse } from 'src/components/transitions/Collapse';
import { useSession } from 'src/contexts/SessionContext';
import {
  CartItem as CartItemType,
  PaymentOption,
} from 'src/models/cart/_types';
import { Cart } from 'src/models/cart/_types';
import { getPaymentOptionDetails } from 'src/models/cart/utils';
import { ProductVariant } from 'src/models/product/_types';
import { getMaxAllowedQuantity } from 'src/models/product/utils';
import { selectableItemTypes } from 'src/shared/constants/itemTypes';
import { usePermissionCheck } from 'src/shared/usePermissionCheck';
import { formatFullDate } from 'src/utils/dateFormatter';
import toast from 'src/utils/toast';

import { CartItemAvatar } from './CartItemAvatar';
import ItemSettings from './ItemSettings';

interface CartItemProps {
  item: CartItemType;
  editable: boolean;
  onRemove?: (item: CartItemType, index?: number) => void;
  index: number;
  onEdit?: (item: CartItemType) => void;
  onChangeQuantity?: (newQuantity) => void;
  onPaymentOptionChange?: (
    item: CartItemType,
    optionId?: string | undefined
  ) => void;
  cart?: Cart | null; // Optional cart for inventory tracking - if not provided, inventory features are disabled
  ItemSettingsComponent?: React.ComponentType<{
    editing?: boolean;
    item: CartItemType;
    itemType?: any;
    onSubmit: (updatedItem: CartItemType) => void;
    user?: any;
  }>;
}

export const CartItem = ({
  item,
  onRemove,
  index,
  onEdit,
  onChangeQuantity,
  onPaymentOptionChange,
  editable,
  cart = null,
  ItemSettingsComponent = ItemSettings,
}: CartItemProps) => {
  const { t } = useTranslation('translation', {
    keyPrefix: 'clients.paymentDialog',
  });
  const { t: tCart } = useTranslation('translation', {
    keyPrefix: 'cart.item',
  });
  const { session, user } = useSession();
  const { canManageAll } = usePermissionCheck();

  // Start expanded if not editable
  const [expanded, setExpanded] = useState(editable ? false : true);

  // Validate quantity change and show error toast if needed
  const handleQuantityChange = (newQuantity: number) => {
    // Allow removal (quantity 0)
    if (newQuantity === 0) {
      onChangeQuantity && onChangeQuantity(newQuantity);
      return;
    }

    // Check stock limits only for product variants with inventory tracking
    // Admin trainers can override stock limits
    if (
      !canManageAll &&
      item.itemType === 'ProductVariant' &&
      item.item &&
      'trackInventory' in item.item
    ) {
      const variant = item.item as unknown as ProductVariant;
      const maxAllowedQuantity = getMaxAllowedQuantity(
        variant,
        cart?.locationId,
        cart,
        index
      );

      if (newQuantity > maxAllowedQuantity) {
        // Show error toast and don't change quantity
        if (maxAllowedQuantity === 0) {
          toast.error(tCart('outOfStock'));
        } else {
          const availableText =
            maxAllowedQuantity === 1
              ? tCart('onlyOneAvailable')
              : tCart('onlyXAvailable', { count: maxAllowedQuantity });
          toast.error(availableText);
        }
        return;
      }
    }

    // If validation passes, proceed with quantity change
    onChangeQuantity && onChangeQuantity(newQuantity);
  };

  // Generate valid quantity options for dropdown
  const getQuantityOptions = useMemo(() => {
    let maxQuantity = 99; // Default maximum

    // Check stock limits for product variants with inventory tracking
    // Admin trainers can override stock limits
    if (
      !canManageAll &&
      item.itemType === 'ProductVariant' &&
      item.item &&
      'trackInventory' in item.item
    ) {
      const variant = item.item as unknown as ProductVariant;
      const maxAllowedQuantity = getMaxAllowedQuantity(
        variant,
        cart?.locationId,
        cart,
        index
      );

      // Limit to available stock but allow at least current quantity
      maxQuantity = Math.max(maxAllowedQuantity, item.quantity);
    }

    return Array.from({ length: Math.min(maxQuantity + 1, 100) }, (_, i) => i);
  }, [item, cart, index, canManageAll]);

  if (!item.item) return null;

  const itemType = selectableItemTypes.find(
    (t) => t.modelName === item.itemType
  );

  const paymentOptions =
    item.itemType === 'onlineTraining'
      ? session?.paymentOptions || []
      : item.item.paymentOptions || [];

  // If no payment options, create a default one-time payment option
  if (paymentOptions.length === 0) {
    const defaultOption: PaymentOption = {
      id: 'default',
      type: 'purchase',
      amount: 0,
      currency: 'usd',
      name: 'One Time Purchase',
      location: null,
      interval: undefined,
      intervalCount: undefined,
    };
    paymentOptions.push(defaultOption);

    // If no payment option is selected, automatically select the default
    if (!item.paymentOption) {
      onPaymentOptionChange &&
        onPaymentOptionChange(item, defaultOption.id || 'default');
    }
  }

  if (item.paymentOption) {
    const foundOption = paymentOptions.find(
      (opt) => opt.id === item.paymentOption?.id
    );
    if (foundOption) {
      foundOption.amount = item.paymentOption.amount;
    } else {
      paymentOptions.push(item.paymentOption);
    }
  }

  return (
    <Card>
      <Stack direction="row" sx={{ p: 2 }}>
        <CartItemAvatar item={item} />
        <Box>
          <Typography variant="subtitle1">
            {item.itemType === 'ClientAssociation'
              ? t('onlineTraining')
              : (item.item as any).name}
          </Typography>
          <Stack direction="column" spacing={0.5}>
            <Stack direction="row" spacing={1} alignItems="center">
              {paymentOptions.length === 1 ? (
                <Typography variant="body2" color="text.secondary">
                  {getPaymentOptionDetails(
                    item.paymentOption || paymentOptions[0]
                  )}
                </Typography>
              ) : (
                <DropdownMenu
                  trigger={
                    <Typography variant="body2" color="text.secondary">
                      {getPaymentOptionDetails(
                        item.paymentOption || paymentOptions[0]
                      )}
                      <Icon
                        size="tiny"
                        sx={{ ml: 0.5 }}
                        icon={faAngleDown}
                      />
                    </Typography>
                  }
                >
                  <Typography
                    color="text.secondary"
                    variant="subtitle2"
                    sx={{ mt: 2, mb: 1, mx: 2 }}
                  >
                    {t('priceOptions')}
                  </Typography>
                  <Stack spacing={1} sx={{ py: 1 }}>
                    {paymentOptions.map((option) => {
                      return (
                        <ListItem
                          key={option.id}
                          rounded
                          clickable
                          primary={getPaymentOptionDetails(option)}
                          selected={item.paymentOption === option.id}
                          onClick={() => {
                            onPaymentOptionChange &&
                              onPaymentOptionChange(item, option.id);
                          }}
                        />
                      );
                    })}
                  </Stack>
                </DropdownMenu>
              )}
            </Stack>
            {/* <CartItemCustomizations item={item} trainers={trainers} /> */}
          </Stack>
        </Box>
      </Stack>
      {editable && (
        <Stack
          direction="row"
          sx={{
            alignItems: 'center',
            justifyContent: 'space-between',
            p: 2,
            pt: 0,
          }}
        >
          <Stack direction="row" sx={{ alignItems: 'center' }}>
            <Button
              onClick={() => setExpanded(!expanded)}
              endIcon={
                <Icon
                  size="tiny"
                  icon={faAngleDown}
                  sx={{
                    transform: expanded ? 'rotate(180deg)' : 'rotate(0deg)',
                    transition: 'transform 0.2s',
                  }}
                />
              }
            >
              {t('details')}
            </Button>

            <Tooltip title={t('customize')}>
              <IconButton onClick={() => onEdit && onEdit(item)} sx={{ ml: 1 }}>
                <Icon
                  size="small"
                  icon={faEdit}
                  color="info"
                />
              </IconButton>
            </Tooltip>

            {item.coupon && (
              <Tooltip title={`${t('couponApplied')} (${item.coupon.code})`}>
                <Icon
                  size="medium"
                  color="info"
                  sx={{ ml: 1 }}
                  icon={faTag}
                />
              </Tooltip>
            )}
          </Stack>

          <Stack direction="column" spacing={1} sx={{ alignItems: 'flex-end' }}>
            <Stack direction="row" spacing={2} sx={{ alignItems: 'center' }}>
              {itemType?.supportsQuantity && (
                <DropdownMenu
                  trigger={
                    <Chip
                      size="small"
                      label={
                        <Stack direction="row" alignItems="center">
                          {item.quantity}
                          <Icon
                            size="small"
                            sx={{ ml: 0.5 }}
                            icon={faAngleDown}
                          />
                        </Stack>
                      }
                    />
                  }
                >
                  <Typography
                    color="text.secondary"
                    variant="subtitle2"
                    sx={{ mt: 2, mb: 1, mx: 2 }}
                  >
                    {t('quantity')}
                  </Typography>
                  {getQuantityOptions.map((i) => (
                    <ListItem
                      key={i}
                      primary={i === 0 ? t('removeFromBag') : i}
                      rounded
                      clickable
                      onClick={() => handleQuantityChange(i)}
                    ></ListItem>
                  ))}
                </DropdownMenu>
              )}
              <IconButton onClick={() => onRemove && onRemove(item, index)}>
                <Icon
                  color="info"
                  icon={faMinusCircle}
                />
              </IconButton>
            </Stack>
            {/* Display remaining quantity for product variants with inventory tracking */}
            {itemType?.supportsQuantity &&
              item.itemType === 'ProductVariant' &&
              item.item &&
              'trackInventory' in item.item &&
              (() => {
                const variant = item.item as unknown as ProductVariant;
                const maxAllowedQuantity = getMaxAllowedQuantity(
                  variant,
                  cart?.locationId,
                  cart,
                  index
                );
                const remainingQuantity = maxAllowedQuantity;

                if (remainingQuantity === Infinity) {
                  return null;
                }

                return (
                  <Typography
                    variant="caption"
                    color="error"
                    sx={{ fontSize: '0.7rem' }}
                  >
                    {remainingQuantity > 0
                      ? tCart('remainingQuantity', {
                          count: remainingQuantity,
                        })
                      : tCart('outOfStock')}
                  </Typography>
                );
              })()}
          </Stack>
        </Stack>
      )}
      <Collapse in={!editable || expanded}>
        <Divider />
        <Box sx={{ p: 2 }}>
          <ItemSettingsComponent
            editing={false}
            item={item}
            itemType={itemType}
            onSubmit={() => {
              // On submit callback
            }}
            user={user}
          />
          {editable && (
            <Button
              fullWidth
              variant="outlined"
              sx={{ mt: 3 }}
              onClick={() => onEdit && onEdit(item)}
            >
              {t('customize')}
            </Button>
          )}
        </Box>
      </Collapse>
    </Card>
  );
};

interface CustomizationItemProps {
  icon: IconDefinition;
  label: string;
  tooltip: string;
  value: string;
}

const CustomizationItem = ({
  icon,
  label,
  tooltip,
  value,
}: CustomizationItemProps) => {
  // Translation handled in parent component
  return (
    <Tooltip title={tooltip || label}>
      <Typography
        variant="caption"
        color="text.secondary"
        sx={{ display: 'flex', alignItems: 'center' }}
      >
        <Icon icon={icon} sx={{ mr: 0.5, fontSize: '0.875rem' }} />
        {value}
      </Typography>
    </Tooltip>
  );
};

interface CartItemCustomizationsProps {
  item: CartItemType;
  trainers?: Array<{ trainerId: string | number; name: string }>;
}

// eslint-disable-next-line @typescript-eslint/no-unused-vars
const CartItemCustomizations = ({
  item,
  trainers,
}: CartItemCustomizationsProps) => {
  const { t } = useTranslation('translation', {
    keyPrefix: 'clients.paymentDialog',
  });
  const hasCustomizations =
    item.startOn ||
    item.cancelDate ||
    item.billingCycleAnchor ||
    item.chargedById ||
    item.overrideProrationBehavior ||
    (item.primaryCommissioneeIds && item.primaryCommissioneeIds.length > 0) ||
    (item.secondaryCommissioneeIds &&
      item.secondaryCommissioneeIds.length > 0) ||
    item.coupon ||
    (item.itemType === 'Fbm::Package' &&
      (item.activationDate || item.expiresOn)) ||
    item.users ||
    item.shareWithAllDependents;

  if (!hasCustomizations) return null;

  return (
    <Stack direction="row" spacing={1} flexWrap="wrap" alignItems="center">
      {item.startOn && (
        <CustomizationItem
          icon={faCalendarDays}
          label={t('startDate')}
          tooltip={t('startDate')}
          value={formatFullDate(item.startOn)}
        />
      )}
      {item.cancelDate && (
        <CustomizationItem
          icon={faCalendarXmark}
          label="Cancel Date"
          tooltip="Cancel Date"
          value={formatFullDate(item.cancelDate)}
        />
      )}
      {item.billingCycleAnchor && (
        <CustomizationItem
          icon={faAnchor}
          label="Billing Cycle Anchor"
          tooltip="Billing Cycle Anchor"
          value={item.billingCycleAnchor.label}
        />
      )}
      {item.chargedById && (
        <CustomizationItem
          icon={faUserTie}
          label="Charged By"
          tooltip="Charged By"
          value={
            trainers?.find((trainer) => trainer.trainerId == item.chargedById)
              ?.name || 'Unknown'
          }
        />
      )}
      {item.overrideProrationBehavior && (
        <CustomizationItem
          icon={faCalculator}
          label="Proration Behavior"
          tooltip="Proration Behavior"
          value={item.overrideProrationBehavior.label}
        />
      )}
      {item.primaryCommissioneeIds &&
        item.primaryCommissioneeIds.length > 0 && (
          <CustomizationItem
            icon={faUserGroup}
            label="Primary Commissionees"
            tooltip={`Primary Commissionees: ${item.primaryCommissioneeIds
              .map(
                (id) =>
                  trainers?.find((trainer) => trainer.trainerId == id)?.name
              )
              .filter(Boolean)
              .join(', ')}`}
            value={`${item.primaryCommissioneeIds.length} Primary`}
          />
        )}
      {item.secondaryCommissioneeIds &&
        item.secondaryCommissioneeIds.length > 0 && (
          <CustomizationItem
            icon={faUserGroup}
            label="Secondary Commissionees"
            tooltip={`Secondary Commissionees: ${item.secondaryCommissioneeIds
              .map(
                (id) =>
                  trainers?.find((trainer) => trainer.trainerId == id)?.name
              )
              .filter(Boolean)
              .join(', ')}`}
            value={`${item.secondaryCommissioneeIds.length} Secondary`}
          />
        )}
      {item.coupon && (
        <CustomizationItem
          icon={faTag}
          label="Coupon Applied"
          tooltip="Coupon Applied"
          value={item.coupon.code}
        />
      )}
      {item.itemType === 'Fbm::Package' && item.activationDate && (
        <CustomizationItem
          icon={faPlay}
          label="Activation Date"
          tooltip="Activation Date"
          value={formatFullDate(item.activationDate)}
        />
      )}
      {item.itemType === 'Fbm::Package' && item.expiresOn && (
        <CustomizationItem
          icon={faHourglassEnd}
          label="Expiration Date"
          tooltip="Expiration Date"
          value={formatFullDate(item.expiresOn)}
        />
      )}
      {item.shareWithAllDependents && (
        <CustomizationItem
          icon={faUsers}
          label="Shared with All Dependents"
          tooltip="Shared with All Dependents"
          value="All Dependents"
        />
      )}
      {item.users && item.users.length > 0 && (
        <CustomizationItem
          icon={faShareNodes}
          label="Shared with Users"
          tooltip={`Shared with: ${item.users
            .map((user) => user.name)
            .join(', ')}`}
          value={`${item.users.length} Users`}
        />
      )}
    </Stack>
  );
};
